import {IntlShape, defineMessages} from 'react-intl'

/**
 * Catálogo de las reglas de auto-push a PolicyDial. La pantalla vive en
 * `pages/autoPush/` pero solo se llega a ella desde la pestaña Campaigns del
 * supervisor de telefonía, así que pertenece a la Fase 1.10 y usa el namespace
 * `telephony.autoPush.*`.
 *
 * No se traduce:
 * - **PolicyDial, VICIdial**: productos.
 * - **Los códigos de disposición** (`SALE`, `DISA`, `DNCL`, `NANS`, `VMAIL`…) y
 *   los nombres de pipeline, etapa, etiqueta, campaña y lista: los sirve el
 *   backend o VICIdial.
 * - **`Lead.callCounter`**, que es el nombre del campo en el modelo.
 * - Los valores de ejemplo de los placeholders (`GA, FL, NC`,
 *   `America/New_York`, `NANS:12, VMAIL:6`).
 *
 * Glosario aplicado (guía §17.14): *dialer* se mantiene en inglés,
 * *report* → "reporte".
 */
export const EM_DASH = '—'

/** Marca de campo obligatorio. */
export const REQUIRED_MARK = '*'

/** "vid:12345" — identificador de VICIdial, no es texto. */
export const vicidialLeadRef = (id: string) => `vid:${id}`

/** "0001 (Ventas)" en el selector de campañas; el nombre solo si viene. */
export const campaignOption = (id: string, name?: string) => (name ? `${id} (${name})` : id)

/** "102 — Leads de agosto" en el selector de listas. */
export const listOption = (id: string, name: string) => `${id} — ${name}`

export const AUTO_PUSH_MESSAGES = defineMessages({
  // ── Cabecera de la página ──
  title: {id: 'telephony.autoPush.title', defaultMessage: 'PolicyDial Auto-Push Rules'},
  subtitle: {
    id: 'telephony.autoPush.subtitle',
    defaultMessage: 'Scheduled lead delivery to VICIdial dialer campaigns. Beat runs every minute.',
  },
  newRule: {id: 'telephony.autoPush.newRule', defaultMessage: 'New rule'},
  tabRules: {id: 'telephony.autoPush.tab.rules', defaultMessage: 'Rules ({count})'},
  tabAudit: {id: 'telephony.autoPush.tab.audit', defaultMessage: 'Audit log'},

  // ── Tabla de reglas ──
  colEnabled: {id: 'telephony.autoPush.column.enabled', defaultMessage: 'Enabled'},
  colTarget: {id: 'telephony.autoPush.column.target', defaultMessage: 'Target'},
  colPriority: {id: 'telephony.autoPush.column.priority', defaultMessage: 'Priority'},
  colEvery: {id: 'telephony.autoPush.column.every', defaultMessage: 'Every'},
  colBatch: {id: 'telephony.autoPush.column.batch', defaultMessage: 'Batch'},
  colWindow: {id: 'telephony.field.window', defaultMessage: 'Window'},
  colLastRun: {id: 'telephony.autoPush.column.lastRun', defaultMessage: 'Last run'},
  colLastPush: {id: 'telephony.autoPush.column.lastPush', defaultMessage: 'Last push'},
  emptyRules: {
    id: 'telephony.autoPush.emptyRules',
    defaultMessage: 'No rules yet. Click <b>New rule</b> to create one.',
  },
  dryRunBadge: {id: 'telephony.autoPush.dryRunBadge', defaultMessage: 'dry-run'},
  listRef: {id: 'telephony.autoPush.listRef', defaultMessage: 'list {id}'},
  everyMinutes: {id: 'telephony.autoPush.everyMinutes', defaultMessage: '{minutes}m'},
  windowRange: {id: 'telephony.autoPush.windowRange', defaultMessage: '{start} → {end}'},
  weekdaysBadge: {id: 'telephony.autoPush.weekdaysBadge', defaultMessage: 'M-F'},
  run: {id: 'telephony.autoPush.run', defaultMessage: 'Run'},

  // ── Toasts y confirmaciones ──
  loadRulesFailed: {
    id: 'telephony.autoPush.loadRulesFailed',
    defaultMessage: 'Failed to load rules',
  },
  toastRuleEnabled: {
    id: 'telephony.autoPush.toast.ruleEnabled',
    defaultMessage: 'Rule enabled',
  },
  toastRuleDisabled: {
    id: 'telephony.autoPush.toast.ruleDisabled',
    defaultMessage: 'Rule disabled',
  },
  toastToggleFailed: {
    id: 'telephony.autoPush.toast.toggleFailed',
    defaultMessage: 'Failed to toggle rule',
  },
  confirmRun: {
    id: 'telephony.autoPush.confirm.run',
    defaultMessage: 'Run rule "{name}" now? This will evaluate leads and push immediately.',
  },
  toastRunDone: {
    id: 'telephony.autoPush.toast.runDone',
    defaultMessage: 'Done: {pushed} pushed, {dryRun} dry-run, {skipped} skipped',
  },
  toastRunFailed: {id: 'telephony.autoPush.toast.runFailed', defaultMessage: 'Run failed'},
  confirmDelete: {
    id: 'telephony.autoPush.confirm.delete',
    defaultMessage: 'Delete rule "{name}"? This cannot be undone.',
  },
  toastRuleDeleted: {
    id: 'telephony.autoPush.toast.ruleDeleted',
    defaultMessage: 'Rule deleted',
  },
  toastDeleteFailed: {
    id: 'telephony.autoPush.toast.deleteFailed',
    defaultMessage: 'Delete failed',
  },
  toastRuleCreated: {
    id: 'telephony.autoPush.toast.ruleCreated',
    defaultMessage: 'Rule created',
  },
  toastRuleUpdated: {
    id: 'telephony.autoPush.toast.ruleUpdated',
    defaultMessage: 'Rule updated',
  },

  // ── Modal: identidad ──
  modalNewTitle: {
    id: 'telephony.autoPush.modal.newTitle',
    defaultMessage: 'New auto-push rule',
  },
  modalEditTitle: {
    id: 'telephony.autoPush.modal.editTitle',
    defaultMessage: 'Edit rule: {name}',
  },
  tabIdentity: {id: 'telephony.autoPush.tab.identity', defaultMessage: 'Identity'},
  tabTarget: {id: 'telephony.autoPush.tab.target', defaultMessage: 'Target campaign'},
  tabSchedule: {id: 'telephony.autoPush.tab.schedule', defaultMessage: 'Schedule'},
  tabFilters: {id: 'telephony.autoPush.tab.filters', defaultMessage: 'Lead filters'},
  tabAntispam: {id: 'telephony.autoPush.tab.antispam', defaultMessage: 'Anti-spam'},
  fieldPriority: {
    id: 'telephony.autoPush.field.priority',
    defaultMessage: 'Priority (lower runs first)',
  },
  fieldEnabled: {
    id: 'telephony.autoPush.field.enabled',
    defaultMessage: 'Enabled (rule runs on schedule)',
  },
  fieldDryRun: {
    id: 'telephony.autoPush.field.dryRun',
    defaultMessage: 'Dry run (log only, do not call VICIdial)',
  },
  errorNameRequired: {
    id: 'telephony.autoPush.error.nameRequired',
    defaultMessage: 'Name is required',
  },
  errorCampaignRequired: {
    id: 'telephony.autoPush.error.campaignRequired',
    defaultMessage: 'Target VICIdial campaign is required',
  },
  errorListRequired: {
    id: 'telephony.autoPush.error.listRequired',
    defaultMessage: 'Select a target list for this campaign',
  },
  errorSaveFailed: {
    id: 'telephony.autoPush.error.saveFailed',
    defaultMessage: 'Save failed',
  },
  createRule: {id: 'telephony.autoPush.createRule', defaultMessage: 'Create rule'},

  // ── Modal: campaña de destino ──
  fieldTargetCampaign: {
    id: 'telephony.autoPush.field.targetCampaign',
    defaultMessage: 'Target VICIdial campaign',
  },
  selectPlaceholder: {id: 'telephony.autoPush.selectPlaceholder', defaultMessage: '— Select —'},
  campaignMustExist: {
    id: 'telephony.autoPush.campaignMustExist',
    defaultMessage: 'Campaign must already exist in VICIdial admin.',
  },
  fieldList: {id: 'telephony.field.list', defaultMessage: 'List'},
  selectCampaignFirst: {
    id: 'telephony.autoPush.selectCampaignFirst',
    defaultMessage: '— Select a campaign first —',
  },
  loadingLists: {id: 'telephony.autoPush.loadingLists', defaultMessage: 'Loading lists...'},
  noListsForCampaign: {
    id: 'telephony.autoPush.noListsForCampaign',
    defaultMessage: 'No active lists for this campaign',
  },
  noListsHint: {
    id: 'telephony.autoPush.noListsHint',
    defaultMessage: 'No active lists found. Create one in VICIdial admin first.',
  },

  // ── Modal: programación ──
  fieldRunEvery: {
    id: 'telephony.autoPush.field.runEvery',
    defaultMessage: 'Run every (minutes)',
  },
  fieldBatchSize: {
    id: 'telephony.autoPush.field.batchSize',
    defaultMessage: 'Max leads per run (batch)',
  },
  fieldHopperTarget: {
    id: 'telephony.autoPush.field.hopperTarget',
    defaultMessage: 'Keep hopper at (optional)',
  },
  fieldWindowStart: {
    id: 'telephony.autoPush.field.windowStart',
    defaultMessage: 'Push only after (local time)',
  },
  fieldWindowEnd: {
    id: 'telephony.autoPush.field.windowEnd',
    defaultMessage: 'Push only before (local time)',
  },
  fieldTimezone: {id: 'telephony.autoPush.field.timezone', defaultMessage: 'Timezone'},
  fieldWeekdaysOnly: {
    id: 'telephony.autoPush.field.weekdaysOnly',
    defaultMessage: 'Skip Saturday and Sunday',
  },

  // ── Modal: filtros de leads ──
  fieldPipeline: {id: 'telephony.autoPush.field.pipeline', defaultMessage: 'Pipeline'},
  anyPipeline: {id: 'telephony.autoPush.anyPipeline', defaultMessage: '— Any pipeline —'},
  fieldStages: {
    id: 'telephony.autoPush.field.stages',
    defaultMessage: 'Stages (empty = all stages of pipeline)',
  },
  pickPipelineFirst: {
    id: 'telephony.autoPush.pickPipelineFirst',
    defaultMessage: 'Pick a pipeline first',
  },
  fieldMaxAgeDays: {
    id: 'telephony.autoPush.field.maxAgeDays',
    defaultMessage: 'Lead created within last N days',
  },
  maxAgeDaysHint: {
    id: 'telephony.autoPush.maxAgeDaysHint',
    defaultMessage: 'e.g. 60 = last 2 months',
  },
  fieldMinAgeMinutes: {
    id: 'telephony.autoPush.field.minAgeMinutes',
    defaultMessage: 'Min lead age (minutes)',
  },
  minAgeMinutesHint: {
    id: 'telephony.autoPush.minAgeMinutesHint',
    defaultMessage: 'Lets intake/dedup process settle before pushing.',
  },
  fieldTagsRequired: {
    id: 'telephony.autoPush.field.tagsRequired',
    defaultMessage: 'Tags REQUIRED (lead must have all)',
  },
  fieldTagsExcluded: {
    id: 'telephony.autoPush.field.tagsExcluded',
    defaultMessage: 'Tags EXCLUDED (lead must have none)',
  },
  fieldStateFilter: {
    id: 'telephony.autoPush.field.stateFilter',
    defaultMessage: 'State filter (comma separated, empty = any)',
  },
  fieldLeadOrdering: {
    id: 'telephony.autoPush.field.leadOrdering',
    defaultMessage: 'Lead ordering',
  },
  newestFirst: {id: 'telephony.autoPush.newestFirst', defaultMessage: 'Newest first'},
  oldestFirst: {id: 'telephony.autoPush.oldestFirst', defaultMessage: 'Oldest first'},

  // ── Modal: anti-spam ──
  fieldMaxAttempts: {
    id: 'telephony.autoPush.field.maxAttempts',
    defaultMessage: 'Max call attempts',
  },
  maxAttemptsHint: {
    id: 'telephony.autoPush.maxAttemptsHint',
    defaultMessage: 'Skip lead if Lead.callCounter ≥ this.',
  },
  fieldCooldown: {
    id: 'telephony.autoPush.field.cooldown',
    defaultMessage: 'Cooldown between pushes (hours)',
  },
  fieldNeverRepush: {
    id: 'telephony.autoPush.field.neverRepush',
    defaultMessage: 'Never re-push if last disposition is in this list',
  },
  neverRepushHint: {
    id: 'telephony.autoPush.neverRepushHint',
    defaultMessage: 'Terminal dispositions, comma separated.',
  },
  fieldRepushAfter: {
    id: 'telephony.autoPush.field.repushAfter',
    defaultMessage: 'Per-disposition cooldown (hours)',
  },
  repushAfterHint: {
    id: 'telephony.autoPush.repushAfterHint',
    defaultMessage:
      'Format: DISPO:hours, comma separated. Wait this many hours before re-pushing after that dispo.',
  },

  // ── Registro de auditoría ──
  auditRule: {id: 'telephony.autoPush.audit.rule', defaultMessage: 'Rule'},
  auditAllRules: {id: 'telephony.autoPush.audit.allRules', defaultMessage: 'All rules'},
  auditResult: {id: 'telephony.autoPush.audit.result', defaultMessage: 'Result'},
  auditAllResults: {id: 'telephony.autoPush.audit.allResults', defaultMessage: 'All results'},
  auditSince: {id: 'telephony.autoPush.audit.since', defaultMessage: 'Since'},
  auditLastHour: {id: 'telephony.autoPush.audit.lastHour', defaultMessage: 'Last hour'},
  auditLast24h: {id: 'telephony.autoPush.audit.last24h', defaultMessage: 'Last 24h'},
  auditLast7d: {id: 'telephony.autoPush.audit.last7d', defaultMessage: 'Last 7d'},
  auditLast30d: {id: 'telephony.autoPush.audit.last30d', defaultMessage: 'Last 30d'},
  auditColWhen: {id: 'telephony.autoPush.audit.column.when', defaultMessage: 'When'},
  auditColLead: {id: 'telephony.autoPush.audit.column.lead', defaultMessage: 'Lead'},
  auditColDetail: {id: 'telephony.autoPush.audit.column.detail', defaultMessage: 'Detail'},
  auditEmpty: {id: 'telephony.autoPush.audit.empty', defaultMessage: 'No pushes found'},
  auditManual: {id: 'telephony.autoPush.audit.manual', defaultMessage: 'manual'},
  auditLoadFailed: {
    id: 'telephony.autoPush.audit.loadFailed',
    defaultMessage: 'Failed to load audit log',
  },

  // ── Reusadas de common.* ──
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  description: {id: 'common.field.description', defaultMessage: 'Description'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
})

/** Resultado de un push. Los valores viajan en la API; se traduce la etiqueta. */
const RESULT = defineMessages({
  success: {id: 'telephony.autoPush.result.success', defaultMessage: 'Success'},
  failure: {id: 'telephony.autoPush.result.failure', defaultMessage: 'Failure'},
  skipped: {id: 'telephony.autoPush.result.skipped', defaultMessage: 'Skipped'},
  dry_run: {id: 'telephony.autoPush.result.dryRun', defaultMessage: 'Dry run'},
})

export const RESULT_VALUES = Object.keys(RESULT) as Array<keyof typeof RESULT>

/** Un resultado nuevo del backend se muestra en crudo en vez de desaparecer. */
export const resultLabel = (intl: IntlShape, result: string): string => {
  const descriptor = RESULT[result as keyof typeof RESULT]
  return descriptor ? intl.formatMessage(descriptor) : result
}
